import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useTranslator from "../../hooks/useTranslator";
import i18next from "i18next";
import constants from "../../Utils/constants";
import AuthContext from "../../context/auth/AuthContext";
import TenantContext from "../../context/tenant/TenantContext";
import "react-toastify/dist/ReactToastify.css";
import { haveValue, toast } from "../../helper/common";
import axios from "axios";
import { useCookies } from "react-cookie";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthSidePanel from "../../components/Auth/AuthSidePanel";
import { Button, Icon } from "../../components/design";
import type { UserRole } from "./authFlow";
import { LanguageSwitch } from "../../components/design";
import { getAppType } from "./authFlow";
import {
  isSkorSdkMode,
  isSdkLangExplicit,
  markSdkLangExplicit,
} from "../../helper/skorSdk";

interface UserTypeCard {
  id: number;
  img: string;
  title: string;
  description: string;
  type: UserRole;
}

const SelectUserType = () => {
  const { _t } = useTranslator();
  const [cookies, setCookie, removeCookie] = useCookies<string>([
    "sharedProfileLink",
  ]);
  const navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const { workspace_slug } = useParams();
  const logincode = queryParameters.get("code");
  const workspaceUuid = queryParameters.get("wid");
  const { hash } = useLocation();

  const { setUserType, toggleLogin, loadUser, setLoading, changeUserLng } =
    useContext(AuthContext);
  const { shareProfile } = useContext(TenantContext);
  const { getWorkspaceDetailsFromSlug } = useContext(LandLordContext);
  const [currentLang, setCurrentLang] = useState(() =>
    isSkorSdkMode() && !isSdkLangExplicit()
      ? "es"
      : localStorage.getItem("i18nextLng")?.toLowerCase() || "en-us"
  );
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<UserRole>("tenant");

  useEffect(() => {
    if (
      haveValue(cookies?.requestProfileLink) &&
      haveValue(cookies?.invitationToken)
    ) {
      navigate("/tenant/auth");
    }
  }, [cookies?.requestProfileLink, cookies?.invitationToken]);

  useEffect(() => {
    if (haveValue(workspace_slug) || haveValue(workspaceUuid)) {
      getWorkspaceDetailsFromSlug(workspace_slug, workspaceUuid)
        .then((res) => {
          const data = res.data;
          if (data.status === "Success") {
            if (
              data?.data?.customLinkEnabled &&
              haveValue(data?.data?.customLinkSlug)
            ) {
              navigate(`/oauth/${data?.data?.customLinkSlug}`);
              if (
                localStorage.getItem("usertype") === "tenant" &&
                haveValue(data?.data?.landlord_id)
              ) {
                setCookie(
                  "requestProfileLink",
                  `${data?.data?.landlord_id}_${data?.data?.uuid}`,
                  {
                    path: "/",
                    maxAge: 604800,
                  }
                );
              }
              setCompanyLogo(data?.data?.invitationLogo);
            } else {
              if (haveValue(data?.data?.invitationLogo)) {
                setCompanyLogo(data?.data?.invitationLogo);
              }
              const url = new URL(window.location.href);
              url.searchParams.delete("wid");

              window.history.replaceState(
                {},
                document.title,
                url.pathname + url.search
              );
            }
            if (data?.data?.catella_rules_activated) {
              setCookie("catellaWorkspaceTenant", `${data?.data?.uuid}`, {
                path: "/",
                maxAge: 3600,
              });
            }
            if (data?.data?.libeen_rules_activated) {
              setCookie("libeenWorkspaceTenant", `${data?.data?.uuid}`, {
                path: "/",
                maxAge: 3600,
              });
            }
            if (data?.data?.dea_rules_activated) {
              setCookie("deaWorkspaceTenant", `${data?.data?.uuid}`, {
                path: "/",
                maxAge: 3600,
              });
            }
          }
        })
        .catch((err) => {
          console.log(err, "Error in getWorkspaceDetailsFromSlug");
        });
    }
  }, [workspace_slug, workspaceUuid]);

  useEffect(() => {
    if (logincode) {
      setLoading(true);
      const user_type = localStorage.getItem("usertype");
      if (!user_type) {
        toast.error(_t("pleaseSelectYourUserType"));
      } else {
        axios
          .get(
            `${process.env.REACT_APP_SERVER_URL}/users/linkedin-login/${logincode}?usertype=${user_type}&user_type=${getAppType()}`,
            {
              headers: {
                "Accept-Language": localStorage.getItem("i18nextLng"),
              },
            }
          )
          .then(async (res) => {
            const serverResponse = res;
            localStorage.setItem("token", serverResponse.data.token);
            localStorage.setItem("is_verified", "true");
            toggleLogin(true);
            await loadUser(user_type);
            setLoading(false);

            if (
              haveValue(cookies.sharedProfileLink) &&
              user_type === "landlord"
            ) {
              if (haveValue(res?.data?.user?._id)) {
                shareProfile(
                  cookies.sharedProfileLink,
                  res?.data?.user?._id,
                  "null",
                  "full",
                  { shareType: "tLink" }
                )
                  .then(() => {
                    removeCookie("sharedProfileLink", {
                      path: "/",
                      maxAge: 1800,
                    });
                  })
                  .catch((err) => {
                    console.log(err);
                  });
              }
            }

            if (localStorage.getItem("usertype") === "tenant") {
              navigate("/tenant");
            } else {
              navigate("/profile-request");
            }
          })
          .catch(() => {
            setLoading(false);
            navigate("/auth");
            toast.error(_t("pleaseSignUpFirstUsingLinkedin"));
          });
      }
    }
  }, [logincode]);

  useEffect(() => {
    const applyLanguageAndRole = async () => {
      // SDK iframe opens in Spanish by default: a stored language from the
      // main app (same-origin localStorage) must not leak into the embedded
      // flow unless the tenant explicitly switched in this tab.
      if (isSkorSdkMode() && !isSdkLangExplicit()) {
        await i18next.changeLanguage("es");
        setCurrentLang("es");
      } else {
        const queryLang = queryParameters.get("lng");
        const storedLang = localStorage.getItem("i18nextLng");

        if (queryLang === "es") {
          await i18next.changeLanguage("es");
          setCurrentLang("es");
        } else if (storedLang === "es") {
          await i18next.changeLanguage("es");
          setCurrentLang("es");
        } else if (storedLang === "en-us") {
          await i18next.changeLanguage("en-us");
          setCurrentLang("en-us");
        } else {
          await i18next.changeLanguage("es");
          setCurrentLang("es");
        }
      }

      if (hash === "#landlord") {
        setUserType("landlord");
      } else if (hash !== "#guarantor") {
        setUserType("tenant");
      }
    };

    applyLanguageAndRole();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const is_verified = localStorage.getItem("is_verified");
    const user_type = localStorage.getItem("usertype");
    if (token && is_verified) {
      toggleLogin(true);
      loadUser(user_type);
      if (user_type === "tenant") {
        navigate("/tenant");
      } else {
        navigate("/profile-request");
      }
    }
  }, [toggleLogin, navigate]);

  const handleLanguageChange = async (langVal: string) => {
    await i18next.changeLanguage(langVal);
    if (isSkorSdkMode()) markSdkLangExplicit();
    await changeUserLng();
    setCurrentLang(langVal);
  };

  const cardData: UserTypeCard[] = [
    {
      id: 1,
      img: "/images/kyc.svg",
      title: _t("tenant"),
      description: _t("createSkorShareWithLandlordAgentForEasyPropertyRenting"),
      type: "tenant",
    },
    {
      id: 2,
      img: "/images/home.svg",
      title: _t("landlordAgent"),
      description: _t("easilyRentYourPropertyToASecureAndTrustedTenant"),
      type: "landlord",
    },
    {
      id: 3,
      img: "/images/guarantor.svg",
      title: _t("guarantor"),
      description: _t("helpYourLovedOneSecureTheirDreamApartment"),
      type: "guarantor",
    },
  ];

  return (
    <div className="bigform tenant-auth-email auth-flat select-role-main">
      <AuthSidePanel role={selectedType} step={1} />
      <div className="form-right new-form-right overflow-y-scroll">
        <div className="logoWrapper tenant-auth-logo-wrapper">
          <LanguageSwitch
            currentLang={currentLang}
            onChange={handleLanguageChange}
          />
        </div>
        <div className="form-inner">
          <div className="auth-step">
            <header className="auth-step-header">
              <h1 className="auth-step-title">{_t("welcomeToSkor")}</h1>
              <p className="auth-step-sub">{_t("loginAs")}:</p>
            </header>
            <div className="d-flex flex-column card-wrapper">
              {cardData.map((item) => (
                <div
                  className={`user-type-card ${
                    selectedType === item.type ? "selected-card" : ""
                  }`}
                  key={item.id}
                  onClick={() => setSelectedType(item.type)}
                >
                  <span className="user-type-icon">
                    <img src={item.img} alt={item.title} />
                  </span>
                  <div className="user-type-card-text">
                    <h2>{item.title}</h2>
                    <p>{item.description}</p>
                  </div>
                  <span className="user-type-radio" aria-hidden="true" />
                </div>
              ))}
            </div>
            <Button
              size="lg"
              block
              iconEnd={<Icon name="arrowRight" size={18} />}
              onClick={() => navigate(`/${selectedType}/auth`)}
            >
              {_t("getStarted")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectUserType;
